import type { TileData } from '../board/board-types';

/** One slot's horizontal extent in viewport coordinates, as measured from the rack. */
export interface SlotSpan {
  left: number;
  right: number;
}

/**
 * Moves the tile at `from` to `to`, shifting the tiles between — an ordinary list reorder. `to` is an
 * index into the list the tile has already been lifted out of, which is what `landingIndex` returns.
 */
export const moveTile = (tiles: TileData[], from: number, to: number): TileData[] => {
  const next = [...tiles];
  const [moved] = next.splice(from, 1);
  next.splice(Math.min(to, next.length), 0, moved);
  return next;
};

/**
 * How far past a tile's centre the pointer carries on before taking its place, and how far back it
 * must come to give that place up again — as a fraction of the tile's own width, so it scales with
 * `cellSize` and with the length of the tile being passed.
 *
 * The two edges are what stop the swap chattering: on a bare centre line, a pointer resting on the
 * cusp hands the place back and forth on every pixel of tremor.
 */
const SWAP_TOLERANCE = 0.15;

/**
 * Where a tile lifted from slot `from` lands when the pointer is at `x`: after every other tile whose
 * centre the pointer has carried past. Half a neighbour plus the tolerance below buys a place, so a
 * tile swaps with its neighbour once it is decidedly more over that neighbour than over its own slot.
 *
 * `held` is where the tile sits in the order being shown right now — `from` before anything has
 * shifted — and is what the tolerance is measured from: a place already taken is kept until the
 * pointer comes back past the near edge of the deadband, and one not yet taken is claimed only past
 * the far edge.
 *
 * `spans` are the slot positions measured once, at pickup, and never re-read from the previewed
 * layout. Rack tiles are not all one width — a three-letter tile is three cells wide — so a preview
 * that measured its own reflow would shift the tiles, find the pointer over a different slot, and
 * shift them back, oscillating for as long as the pointer sat on the seam.
 */
export const landingIndex = (spans: SlotSpan[], from: number, x: number, held: number): number =>
  spans
    .filter((_, index) => index !== from)
    .filter((span, place) => {
      const centre = (span.left + span.right) / 2;
      const tolerance = (span.right - span.left) * SWAP_TOLERANCE;
      return x > centre + (place < held ? -tolerance : tolerance);
    }).length;
